import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, MagnifyingGlass, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";

/** ShareVoiceSheet — DM an AI clip / recording as a voice bubble. clip = { path, url, duration, title }. */
export default function ShareVoiceSheet({ clip, onClose }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setUsers([]); return; }
    const t = setTimeout(() => api.get("/users/search", { params: { q } }).then((r) => setUsers(r.data.slice(0, 8))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const send = async (u) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/messages", { recipient_id: u.id, text: "", voice_path: clip.path, voice_duration: clip.duration, voice_title: clip.title });
      toast.success(<span>Voice clip sent to @{u.username} · <Link to={`/messages/${u.id}`} className="underline">Open chat</Link></span>);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  if (!clip) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-end" data-testid="share-voice-sheet">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full bg-ink rounded-t-3xl border-t border-white/10 p-5 max-h-[75vh] overflow-y-auto animate-fade-up">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-cream text-xl font-black tracking-tight flex items-center gap-2"><PaperPlaneTilt size={20} weight="fill" className="text-papaya" /> Share voice clip</h3>
          <button onClick={onClose} data-testid="share-voice-close" className="p-2 rounded-full hover:bg-white/5"><X size={20} className="text-cream" /></button>
        </div>
        <p className="text-cream/50 text-xs mb-4 truncate">“{clip.title}” · sends as a playable voice bubble in a DM</p>
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5">
          <MagnifyingGlass size={16} className="text-cream/50" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" data-testid="share-voice-search" className="flex-1 bg-transparent text-cream placeholder:text-cream/40 focus:outline-none text-sm" />
        </div>
        <div className="mt-3 space-y-2">
          {users.map((u) => (
            <button key={u.id} disabled={busy} onClick={() => send(u)} data-testid={`share-voice-to-${u.username}`} className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/50 text-left">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black flex-shrink-0">
                {u.avatar_path ? <img src={fileUrl(u.avatar_path)} alt="" className="w-full h-full object-cover" /> : (u.display_name || u.username)[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-cream font-bold text-sm truncate">{u.display_name}</p>
                <p className="text-cream/50 text-xs">@{u.username}</p>
              </div>
              <span className="text-papaya text-[10px] font-black tracking-[0.2em] uppercase">Send</span>
            </button>
          ))}
          {q && users.length === 0 && <p className="text-cream/40 text-sm">No people found.</p>}
        </div>
      </div>
    </div>
  );
}
